import type { TextOnPathConfig } from '@/lib/shapes/types/text-on-path-config'
import { hasTextOnPathContent } from '@/lib/shapes/text-on-path-session'
import type { TextOnPathHandleDrag } from '../hooks/use-text-on-path-handle-drag'
import type { TextOnPathPreview } from '../hooks/use-text-on-path-preview'
import { TextOnPathStartDot } from './text-on-path-start-dot'
import { TextOnPathText } from './text-on-path-text'

interface TextOnPathLayerProps {
  shapeId: string
  shapePath: string
  config: TextOnPathConfig
  preview: TextOnPathPreview
  isEditing: boolean
  /** False when nothing listens for start-distance changes: the start dot is then display-only. */
  draggable: boolean
  drag: TextOnPathHandleDrag
}

/** The path definitions, the text riding them, and (while editing) the start dot. */
export function TextOnPathLayer({
  shapeId,
  shapePath,
  config,
  preview,
  isEditing,
  draggable,
  drag,
}: TextOnPathLayerProps) {
  const pathId = `text-on-path-${shapeId}`
  const textPathId = `${pathId}-text`
  const { textD, startOffset, offset } = preview.direction

  return (
    <>
      <defs>
        {/*
          The text runs along the SAME `d` string the shape draws, so it inherits the renderer's
          path-resolution priority (rounding → pen path → pathCommands → WASM) and the shell's
          viewBox for free. Sampling the geometry ourselves would mean re-deriving all of that plus
          a canvas→screen projection.
        */}
        <path ref={preview.pathRef} id={pathId} d={shapePath} fill="none" />
        {/*
          The path the text actually rides: the outline reversed (counter-clockwise), moved along
          its own normal by the Offset, or both. Emitted only when it differs from the outline, so
          an un-flipped shape at offset 0 carries no extra node.
        */}
        {textD != null && <path id={textPathId} d={textD} fill="none" />}
      </defs>
      {hasTextOnPathContent(config) && (
        <TextOnPathText
          config={config}
          shapeId={shapeId}
          pathId={textD != null ? textPathId : pathId}
          startOffset={startOffset}
          offset={offset}
        />
      )}
      {/*
        Edit-mode chrome: the blue start dot, and nothing else.

        AC-3 asks for "the object's path in red" while the bar is open, but the Figma flow spec
        (node 20459:490793) draws no such outline in any of its fourteen frames — the object keeps
        its own stroke throughout and the only chrome is this dot. Following the spec; the AC needs
        amending.
      */}
      {isEditing && preview.startDot && (
        <TextOnPathStartDot
          center={preview.startDot}
          radius={preview.startDotRadius}
          draggable={draggable}
          drag={drag}
        />
      )}
    </>
  )
}
